import api from './api';

/**
 * Event service for API calls
 */
const eventService = {
  /**
   * Get public events
   */
  async getPublicEvents(params = {}) {
    const response = await api.get('/events/public', { params });
    return response.data;
  },

  /**
   * Get all events (authenticated)
   */
  async getEvents(params = {}) {
    const response = await api.get('/events', { params });
    return response.data;
  },

  /**
   * Get event by ID
   */
  async getEventById(id) {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },

  /**
   * Get event by slug
   */
  async getEventBySlug(slug) {
    const response = await api.get(`/events/slug/${slug}`);
    return response.data;
  },

  /**
   * Create event
   */
  async createEvent(eventData) {
    const response = await api.post('/events', eventData);
    return response.data;
  },

  /**
   * Update event
   */
  async updateEvent(id, eventData) {
    const response = await api.put(`/events/${id}`, eventData);
    return response.data;
  },

  /**
   * Delete event
   */
  async deleteEvent(id) {
    const response = await api.delete(`/events/${id}`);
    return response.data;
  },

  /**
   * Publish event
   */
  async publishEvent(id) {
    const response = await api.post(`/events/${id}/publish`);
    return response.data;
  },

  /**
   * Clone event
   */
  async cloneEvent(id, name) {
    const response = await api.post(`/events/${id}/clone`, { name });
    return response.data;
  },

  /**
   * Register for event
   */
  async registerForEvent(id) {
    const response = await api.post(`/events/${id}/register`);
    return response.data;
  },

  /**
   * Unregister from event
   */
  async unregisterFromEvent(id) {
    const response = await api.delete(`/events/${id}/register`);
    return response.data;
  },

  /**
   * Get event attendees
   */
  async getAttendees(eventId, params = {}) {
    const response = await api.get(`/events/${eventId}/attendees`, { params });
    return response.data;
  },

  /**
   * Check in attendee
   */
  async checkInAttendee(eventId, userId) {
    const response = await api.post(`/events/${eventId}/attendees/${userId}/check-in`);
    return response.data;
  },

  /**
   * Get user's registered events
   */
  async getUserRegisteredEvents(params = {}) {
    const response = await api.get('/events/user/registered', { params });
    return response.data;
  },

  /**
   * Get user's created events
   */
  async getUserCreatedEvents() {
    const response = await api.get('/events/user/created');
    return response.data;
  },

  /**
   * Update event registration status
   */
  async updateRegistrationStatus(eventId, status, message = null) {
    const response = await api.put(`/events/${eventId}/registration-status`, { status, message });
    return response.data;
  },

  /**
   * Update event registration schedule
   */
  async updateRegistrationSchedule(eventId, schedule) {
    const response = await api.put(`/events/${eventId}/registration-schedule`, schedule);
    return response.data;
  },

  /**
   * Clear event registration schedule
   */
  async clearRegistrationSchedule(eventId) {
    const response = await api.delete(`/events/${eventId}/registration-schedule`);
    return response.data;
  },

  /**
   * Register via multi-step wizard
   */
  async registerViaWizard(eventId, data) {
    const response = await api.post(`/events/${eventId}/register-wizard`, data);
    return response.data;
  },

  /**
   * Get registration status for current user
   */
  async getRegistrationStatus(eventId) {
    const response = await api.get(`/events/${eventId}/registration-status`);
    return response.data;
  },

  /**
   * Unlock an attendee's registration (organizer action)
   */
  async unlockRegistration(eventId, userId) {
    const response = await api.put(`/events/${eventId}/attendees/${userId}/unlock`);
    return response.data;
  },

  /**
   * Get the current user's check-in token for an event
   */
  async getMyCheckInToken(eventId) {
    const response = await api.get(`/events/${eventId}/my-check-in-token`);
    return response.data;
  },

  /**
   * Check in an attendee by their QR token (event-level)
   * @param {number} eventId - The event ID
   * @param {string} token - The attendee's check-in token
   */
  async checkInByToken(eventId, token) {
    const response = await api.post(`/events/${eventId}/check-in-by-token`, { token });
    return response.data;
  },

  /**
   * Toggle check-in enabled/disabled for an event
   * @param {number} eventId - The event ID
   * @param {boolean} enabled - Whether check-in should be enabled
   */
  async toggleCheckIn(eventId, enabled) {
    const response = await api.put(`/events/${eventId}/check-in-toggle`, { enabled });
    return response.data;
  },

  /**
   * Set attendance override for a user's sessions
   * @param {number} eventId - The event ID
   * @param {number} userId - The target user ID
   * @param {boolean|null} override - true=attended, false=not attended, null=clear
   * @param {number} [sessionId] - Optional specific session ID; omit for all sessions
   */
  async setAttendanceOverride(eventId, userId, override, sessionId = undefined) {
    const body = { userId, override };
    if (sessionId !== undefined) {
      body.sessionId = sessionId;
    }
    const response = await api.put(`/events/${eventId}/attendance-override`, body);
    return response.data;
  },

  /**
   * Get lunch options for an event
   */
  async getLunchOptions(eventId) {
    const response = await api.get(`/events/${eventId}/lunch-options`);
    return response.data;
  },

  /**
   * Create a lunch option for an event
   */
  async createLunchOption(eventId, data) {
    const response = await api.post(`/organizer/events/${eventId}/lunch-options`, data);
    return response.data;
  },

  /**
   * Update a lunch option
   */
  async updateLunchOption(eventId, optionId, data) {
    const response = await api.put(`/organizer/events/${eventId}/lunch-options/${optionId}`, data);
    return response.data;
  },

  /**
   * Delete a lunch option
   */
  async deleteLunchOption(eventId, optionId) {
    const response = await api.delete(`/organizer/events/${eventId}/lunch-options/${optionId}`);
    return response.data;
  },

  /**
   * Reorder lunch options
   */
  async reorderLunchOptions(eventId, optionIds) {
    const response = await api.put(`/organizer/events/${eventId}/lunch-options/reorder`, { optionIds });
    return response.data;
  },

  /**
   * List the current user's period opt-outs (Not Attending markers) for an event.
   */
  async getPeriodOptOuts(eventId) {
    const response = await api.get(`/events/${eventId}/period-opt-outs`);
    return response.data;
  },

  /**
   * Mark the current user as not attending a given time period on a given date.
   * Auto-removes any sessions in that period+date from the schedule.
   */
  async setPeriodOptOut(eventId, timePeriodId, date) {
    const response = await api.post(`/events/${eventId}/period-opt-outs`, {
      timePeriodId,
      date,
    });
    return response.data;
  },

  /**
   * Clear a "Not Attending" marker for a given time period on a given date.
   */
  async clearPeriodOptOut(eventId, timePeriodId, date) {
    const response = await api.delete(`/events/${eventId}/period-opt-outs`, {
      data: { timePeriodId, date },
    });
    return response.data;
  },

  /**
   * Update early bird registration settings for an event (organizer only).
   *
   * @param {number|string} eventId - The event ID
   * @param {object} settings - { enabled, opensAt, closesAt, password }
   *   - enabled: boolean
   *   - opensAt: ISO UTC string (or null)
   *   - closesAt: ISO UTC string (or null)
   *   - password: string (new password) | null (clear existing) | undefined (leave unchanged)
   * @returns {Promise<object>} The updated event object
   */
  async updateEarlyBirdSettings(eventId, settings) {
    const response = await api.put(`/events/${eventId}/early-bird`, settings);
    return response.data.data;
  },

  /**
   * Verify the early bird password for an event and receive a short-lived
   * JWT grant token for use in the registration wizard.
   *
   * @param {number|string} eventId - The event ID
   * @param {string} password - The plaintext early bird password
   * @returns {Promise<{ token: string }>} Grant token payload
   */
  async verifyEarlyBirdPassword(eventId, password) {
    const response = await api.post(`/events/${eventId}/verify-early-bird-password`, { password });
    return response.data.data;
  },
};

export default eventService;
